import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import Menu from "../../panel/Menu";
import moment from "moment";

import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Pagination,
  Chip,
  CircularProgress
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";

function Checkouts() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000
  });

  const [bookings, setBookings] = useState([]);
  const [propertyId, setPropertyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  // Pagination
  const rowsPerPage = 10;
  const [page, setPage] = useState(1);

  // ================= STEP 1: Get User Property ID =================
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const roleRef = doc(db, "authroles", user.uid);
          const snap = await getDoc(roleRef);
          if (snap.exists()) {
            setPropertyId(snap.data().propertyId);
          }
        } catch (error) {
          console.error("Error fetching role:", error);
        }
      }
    });
    return () => unsub();
  }, []);

  // ================= STEP 2: Fetch Data with propertyId =================
  useEffect(() => {
    if (!propertyId) return;

    setLoading(true);
    try {
      // Added propertyId filter to the query
      const q = query(
        collection(db, "reservations"),
        where("propertyId", "==", propertyId),
        where("status", "==", "Checkout"),
        orderBy("checkoutdate", "desc"),
        limit(500) // Adjusted limit for better performance
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        setBookings(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            data: doc.data()
          }))
        );
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      Toast.fire({ icon: "error", title: error.message });
      setLoading(false);
    }
  }, [propertyId]);

  // ================= STEP 3: Optimized Filtering =================
  const filteredBookings = useMemo(() => {
    return bookings.filter((item) =>
      item.data.customers.custname
        .toUpperCase()
        .includes(searchText.toUpperCase()) ||
      item.data.customers.mobile.includes(searchText) ||
      item.data.invoiceno?.includes(searchText)
    );
  }, [searchText, bookings]);

  const paginatedData = filteredBookings.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ display: "flex", bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      <Menu />

      <Box className="content-wrapper" component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={800} color="#0a2540">
            Checkout History
          </Typography>
          <Chip 
            label={`Total Records: ${filteredBookings.length}`} 
            color="primary" 
            sx={{ fontWeight: 700, borderRadius: '8px' }} 
          />
        </Stack>

        <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", border: "none" }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} mb={3}>
              <TextField
                size="small"
                placeholder="Search name, mobile or invoice..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1); // Reset to page 1 on search
                }}
                sx={{ width: 400, bgcolor: "#fff" }}
              />
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee", borderRadius: 3 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Sr No</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Stay Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Rooms</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Paid</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "#f8fafc" }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                        <CircularProgress size={30} />
                        <Typography sx={{ mt: 2, color: "text.secondary" }}>Fetching History...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <TableRow hover key={item.id}>
                        <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight={600}>#{item.data.invoiceno}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ textTransform: "capitalize", fontWeight: 700 }}>
                            {item.data.customers.custname}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {item.data.customers.mobile}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            IN: {moment(item.data.checkindate).format("DD MMM")}
                          </Typography>
                          <Typography variant="caption" display="block" fontWeight={700}>
                            OUT: {moment(item.data.checkoutdate).format("DD MMM")}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.data.rooms.map((r, i) => (
                            <Chip
                              key={i}
                              label={r.roomno}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5, fontSize: '0.65rem', fontWeight: 700 }}
                            />
                          ))}
                        </TableCell>
                        <TableCell align="right">
                          ₹{item.data.totalamount?.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "success.main", fontWeight: 700 }}>
                          ₹{item.data.advance?.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            href={`/print-invoice/${item.id}`}
                            sx={{ bgcolor: "aliceblue" }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                        <Typography color="textSecondary">No checkout records found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredBookings.length > rowsPerPage && (
              <Stack alignItems="center" mt={4}>
                <Pagination
                  count={Math.ceil(filteredBookings.length / rowsPerPage)}
                  page={page}
                  onChange={(e, val) => setPage(val)}
                  color="primary"
                  shape="rounded"
                />
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Checkouts;